import { defineStore, skipHydrate } from 'pinia';
import { useUserStore } from './user';
import { getStorageSync, setStorageSync, removeStorageSync } from '@/utils/storage.js';
import { clearClientAuthStorage } from '@/utils/authState';

const COOKIE_KEY = 'cmdc_access_token_mobile';

export const useAuthStore = defineStore('auth', () => {
	const userStore = useUserStore();
	const accessToken = ref<string | null>(null);
	const tokenCookie = useCookie<string | null>(COOKIE_KEY, {
		maxAge: 60 * 60 * 24 * 7,
		sameSite: 'lax',
	});

	const cookieToken = tokenCookie.value;
	if (cookieToken) {
		accessToken.value = cookieToken;
	}

	if (import.meta.client) {
		try {
			const stored = (getStorageSync(COOKIE_KEY) as string | null) ?? cookieToken;
			if (stored) {
				accessToken.value = stored;
			}
		} catch (e) {
			console.error('Failed to read accessToken from localStorage', e);
		}
	}

	watch(accessToken, newVal => {
		if (newVal) {
			if (import.meta.client) {
				setStorageSync(COOKIE_KEY, newVal);
			}
			tokenCookie.value = newVal;
		} else {
			if (import.meta.client) {
				removeStorageSync(COOKIE_KEY);
			}
			tokenCookie.value = null;
		}
	});

	const loginInfo = computed(() => userStore.loginInfo);
	const role = ref<'user' | 'admin' | 'vendor' | 'hospital'>('user');
	const isLoggedIn = computed(() => !!accessToken.value);

	function setToken(newToken: string) {
		accessToken.value = newToken;
	}

	function clearToken() {
		accessToken.value = null;
		tokenCookie.value = null;
		userStore.clearLoginInfo();
		clearClientAuthStorage();
	}

	async function login(credentials: any) {
		try {
			const { login } = await import('~/api/auth');
			const res = await login(credentials);
			if (res && res.code === '200') {
				accessToken.value = res.data.accessToken;
				userStore.setLoginInfo(res.data);
				return true;
			}

			console.error('Login failed with code:', res?.code, res?.message);
			return false;
		} catch (e) {
			console.error('Login failed', e);
			return false;
		}
	}

	function logout() {
		clearToken();
		// 清理 URL 中的微信回调参数，避免跳转首页时 SSR 解析报错
		if (import.meta.client) {
			const url = new URL(window.location.href);
			if (url.searchParams.has('code') || url.searchParams.has('state')) {
				url.searchParams.delete('code');
				url.searchParams.delete('state');
				window.history.replaceState({}, '', url.toString());
			}
		}
		navigateTo('/');
	}

	function reset() {
		clearToken();
	}

	return {
		accessToken: skipHydrate(accessToken),
		loginInfo,
		role,
		isLoggedIn,
		setToken,
		clearToken,
		login,
		logout,
		reset,
	};
});
